/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { VehicleBackAvailableDTO } from '../models/VehicleBackAvailableDTO';
import type { DocumentDeleteRequestDTO } from '../models/DocumentDeleteRequestDTO';
import type { VehicleCreateRequestDTO } from '../models/VehicleCreateRequestDTO';
import type { VehicleEntity } from '../models/VehicleEntity';
import type { VehicleFilterDTO } from '../models/VehicleFilterDTO';
import type { VehicleUpdateRequestDTO } from '../models/VehicleUpdateRequestDTO';

import { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
import type { VehicleLastInsertedDTO } from '../models/VehicleLastInsertedDTO';
import type { VehicleQuickoQuotationRequestDTO } from '../models/VehicleQuickoQuotationRequestDTO';
import type { VehicleInEvidenzaDTO } from '../models/VehicleInEvidenzaDTO';
import type { VehicleWishlistDTO } from '../models/VehicleWishlistDTO';
import type { InformationRequestDTO } from '../models/InformationRequestDTO';
import type { VehicleQuickoCalcCommissionsDTO } from '../models/VehicleQuickoCalcCommissionsDTO';
import type { VehicleQuickoCommissionsRequestDTO } from '../models/VehicleQuickoCommissionsRequestDTO';
import { ReporterVisibilityDTO } from '../models/ReporterVisibilityDTO';
import { VehicleMassiveUpdateRequestDTO } from '../models/VehicleMassiveUpdateDTO';
import { secureImageList } from 'src/utils';
import { isMarket } from 'src/utils/markets';
import { Markets } from 'src/types';

export class VehicleControllerService {
	constructor(public readonly httpRequest: BaseHttpRequest) {}

	// We do not use this, TODO: delete when confirmed
	/**
	 * @returns VehicleEntity OK
	 * @throws ApiError
	 */
	public getVehicles(): CancelablePromise<Array<VehicleEntity>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @param requestBody
	 * @returns any OK
	 * @throws ApiError
	 */
	public postVehicles(
		requestBody?: VehicleCreateRequestDTO
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/vehicles',
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @returns VehicleBackAvailableDTO OK
	 * @throws ApiError
	 */
	public getVehiclesBackAvailable(): CancelablePromise<
		Array<VehicleBackAvailableDTO>
	> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/back-available',
		});
	}

	/**
	 * @returns VehicleBackAvailableDTO OK
	 * @throws ApiError
	 */
	public getLastestInsertedVehicle(): CancelablePromise<
		Array<VehicleLastInsertedDTO>
	> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/latest-ins',
		});
	}

	/**
	 * @returns VehicleFilterDTO OK
	 * @throws ApiError
	 */
	// NB!!! use the hook fetchVehiclesRecursively to cut the request in chunks cause aws lambda limit response of 6mb
	public getVehiclesFilter(
		data_inserimento_from?: string,
		data_inserimento_to?: string,
		skip?: number,
		limit?: number,
	): CancelablePromise<Array<VehicleFilterDTO>> {
		const query: Record<string, string> = {};
		const headers = { 'Accept-Encoding': 'gzip' };

		if (data_inserimento_from) {
			query['data_inserimento_from'] = data_inserimento_from;
		}

		if (data_inserimento_to) {
			query['data_inserimento_to'] = data_inserimento_to;
		}

		if (skip !== null && skip !== undefined) {
			query['skip'] = String(skip);
			skip > 0 && (headers['x-skip-loading'] = '1');
		}

		if (limit !== null && limit !== undefined) {
			query['limit'] = String(limit);
		}

		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/filter',
			query,
			headers,
		});
	}

	public getVehiclesFilterV2(
		data_inserimento_from?: string,
		data_inserimento_to?: string,
		skip?: number,
		limit?: number,
	): CancelablePromise<{ data: VehicleFilterDTO[]; totalCount: number | null }> {
		const query: Record<string, string> = {};
		const reqHeaders: Record<string, string> = { 'Accept-Encoding': 'gzip' };

		if (data_inserimento_from) {
			query['data_inserimento_from'] = data_inserimento_from;
		}

		if (data_inserimento_to) {
			query['data_inserimento_to'] = data_inserimento_to;
		}

		if (skip !== null && skip !== undefined) {
			query['skip'] = String(skip);
			skip > 0 && (reqHeaders['x-skip-loading'] = '1');
		}

		if (limit !== null && limit !== undefined) {
			query['limit'] = String(limit);
		}

		const source = this.httpRequest.request<{ data: VehicleFilterDTO[]; headers: Record<string, string> }>({
			method: 'GET',
			url: '/vehicles/filter',
			query,
			headers: reqHeaders,
			responseHeaders: ['X-Total-Count'],
		});

		return new CancelablePromise((resolve, reject, onCancel) => {
			onCancel(() => source.cancel());
			source.then(
				({ data, headers }) => {
					const raw = headers['X-Total-Count'];
					const totalCount = raw != null ? parseInt(raw, 10) : null;
					resolve({ data, totalCount });
				},
				reject,
			);
		});
	}

	public getMyFleetVehiclesFilter(): CancelablePromise<Array<VehicleEntity>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/myfleet',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @param id
	 * @returns VehicleEntity OK
	 * @throws ApiError
	 */
	public getVehicles1(id: string): CancelablePromise<VehicleEntity> {
		// @ts-ignore
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/{id}',
			path: {
				id: id,
			},
		}).then((vehicle: any) => {
			try {
				if (isMarket(Markets.it)) {
					return vehicle;
				}

					if (!vehicle?.image_list?.length && !vehicle?.images?.length) {
					return vehicle;
				}

				return {
				...vehicle,
				...(vehicle.image_list?.length
					? { image_list: secureImageList(vehicle.image_list) }
					: {}),
				...(vehicle.images?.length ? { images: secureImageList(vehicle.images) } : {}),
				};
			} catch (error) {
				console.warn(error);
				return vehicle;
			}
		});
	}

	// api teast force error
	/**
	 * @param id
	 * @returns VehicleEntity OK
	 * @throws ApiError
	 */
	public getVehicles2(id: string): CancelablePromise<VehicleEntity> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehiclefg/{id}',
			path: {
				id: id,
			},
		});
	}

	/**
	 * @param id
	 * @param requestBody
	 * @returns any OK
	 * @throws ApiError
	 */
	public putVehicles(
		id: string,
		requestBody?: VehicleUpdateRequestDTO
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: '/vehicles/{id}',
			path: {
				id: id,
			},
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @param id
	 * @param requestBody
	 * @returns any OK
	 * @throws ApiError
	 */
	public deleteDocument(
		id: string,
		requestBody?: DocumentDeleteRequestDTO
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: '/vehicles/document-delete/{id}',
			path: {
				id: id,
			},
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @param id
	 * @returns any OK
	 * @throws ApiError
	 */
	public deleteVehicles(id: string): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'DELETE',
			url: '/vehicles/{id}',
			path: {
				id: id,
			},
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @returns VehicleWishlistDTO OK
	 * @throws ApiError
	 */
	public getWishlist(
		username: string
	): CancelablePromise<Array<VehicleWishlistDTO>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/wishlist/{username}',
			path: {
				username: username,
			},
		});
	}

	public modifyVisibility(
		body: ReporterVisibilityDTO
	): CancelablePromise<Array<VehicleWishlistDTO>> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/vehicles/visibilita-segnalatore',
			body,
		});
	}

	/**
	 * @param id
	 * @returns any OK
	 * @throws ApiError
	 */
	public postWishlist(id: string): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/vehicles/wishlist/{id}',
			path: {
				id: id,
			},
		});
	}

	/**
	 * @param id
	 * @returns any OK
	 * @throws ApiError
	 */
	public deleteWishlist(id: string): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'DELETE',
			url: '/vehicles/wishlist/{id}',
			path: {
				id: id,
			},
		});
	}

	//  * @returns VehicleInEvidenzaDTO OK
	/**
	 * @returns any OK
	 * @throws ApiError
	 */
	public getVehiclesInEvidenza(): CancelablePromise<
		Array<VehicleInEvidenzaDTO>
	> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/in-evidenza',
		});
	}

	/**
	 * @param requestBody
	 * @returns any OK
	 * @throws ApiError
	 */
	public sendQuationRequest(
		id: string,
		requestBody?: InformationRequestDTO
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: '/vehicles/request-quotation/{id}',
			body: requestBody,
			mediaType: 'application/json',
			path: {
				id: id,
			},
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @param requestBody
	 * @returns any OK
	 * @throws ApiError
	 */
	public getQuotationRequests(username: string): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/request-quotation/{username}',
			mediaType: 'application/json',
			path: {
				username: username,
			},
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @param requestBody
	 * @returns any OK
	 * @throws ApiError
	 */
	public getQuotationRequestsForFleetManager(): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/request-quotation',
			mediaType: 'application/json',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @param requestBody
	 * @returns any OK
	 * @throws ApiError
	 */
	public quickoQuotation(
		requestBody?: VehicleQuickoQuotationRequestDTO
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/vehicles/quicko-quotation',
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public quickoCommissions(
		requestBody?: VehicleQuickoCommissionsRequestDTO
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/vehicles/quicko-calc-commissions',
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @param requestBody
	 * @returns any OK
	 * @throws ApiError
	 */
	public calcQuickoCommissions(requestBody?: any): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/vehicles/quicko-calc-commissions',
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getQuotationParameters(jatoVehicleId: number): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/quotation-parameters',
			query: { jatoVehicleId },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	/**
	 * @param id
	 * @param requestBody
	 * @returns any OK
	 * @throws ApiError
	 */
	public putBatchVehicles(
		requestBody?: Partial<VehicleMassiveUpdateRequestDTO>
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: '/vehicles/batch-update',
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

}
